/**
 * TutorialTooltip — what the step says, parked next to what it points at.
 *
 * `data-tutorial-ui` on the root is what tells the click shield to let this
 * through; without it the tutorial could not be dismissed while it was running.
 */

import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { arrow, autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react-dom';
import styles from './TutorialTooltip.module.css';

const OPPOSITE = { top: 'bottom', right: 'left', bottom: 'top', left: 'right' };

const TutorialTooltip = ({
    step,
    stepIndex,
    stepCount,
    hint,
    anchorEl,
    resolveError,
    body,
    onNext,
    onExit,
    onRetry,
}) => {
    const arrowRef = useRef(null);
    const rootRef = useRef(null);

    const { refs, floatingStyles, middlewareData, placement } = useFloating({
        placement: step?.placement || 'bottom',
        middleware: [offset(14), flip({ padding: 12 }), shift({ padding: 12 }), arrow({ element: arrowRef })],
        whileElementsMounted: autoUpdate,
    });

    useLayoutEffect(() => {
        refs.setReference(anchorEl || null);
    }, [anchorEl, refs]);

    // Move focus to the tooltip on every step so a keyboard user follows along;
    // the shield swallows keystrokes everywhere else anyway.
    useEffect(() => {
        rootRef.current?.focus?.();
    }, [step?.id]);

    const handleKeyDown = (event) => {
        // Keep Tab inside the tooltip: nothing outside it is actionable.
        if (event.key !== 'Tab') return;
        const focusable = rootRef.current?.querySelectorAll('button');
        if (!focusable || focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    };

    const floating = anchorEl ? floatingStyles : undefined;
    const arrowX = middlewareData.arrow?.x;
    const arrowY = middlewareData.arrow?.y;
    const arrowSide = OPPOSITE[placement.split('-')[0]];

    const setRefs = (node) => {
        rootRef.current = node;
        refs.setFloating(node);
    };

    return (
        <div
            data-tutorial-ui="tooltip"
            ref={setRefs}
            className={`${styles.tooltip} ${anchorEl ? '' : styles.centered}`}
            style={floating}
            role="dialog"
            aria-modal="false"
            aria-label={step?.title || 'Tutorial'}
            tabIndex={-1}
            onKeyDown={handleKeyDown}
        >
            {anchorEl && (
                <div
                    ref={arrowRef}
                    className={styles.arrow}
                    style={{
                        left: arrowX != null ? `${arrowX}px` : '',
                        top: arrowY != null ? `${arrowY}px` : '',
                        [arrowSide]: '-5px',
                    }}
                />
            )}

            <h2 className={styles.title}>{step?.title}</h2>

            <div className={styles.body} aria-live="polite">
                {typeof body === 'string' ? <p>{body}</p> : body}
            </div>

            {resolveError && (
                <div className={styles.hint}>
                    Could not find what this step points at. Try again, or exit the tutorial.
                </div>
            )}

            {hint && !resolveError && <div className={styles.hint}>{hint}</div>}

            <div className={styles.footer}>
                <span className={styles.counter}>
                    Step {stepIndex + 1} of {stepCount}
                </span>
                <div className={styles.actions}>
                    <button type="button" className={styles.exitButton} onClick={onExit}>
                        Exit tutorial
                    </button>
                    {resolveError ? (
                        <button type="button" className={styles.nextButton} onClick={onRetry}>
                            Try again
                        </button>
                    ) : step?.advanceOn?.type === 'next' ? (
                        <button type="button" className={styles.nextButton} onClick={onNext}>
                            Next
                        </button>
                    ) : (
                        <span className={styles.waitNote}>Your turn</span>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TutorialTooltip;
